import { useEffect, useState } from 'react';
import { Button, DialogCloseTrigger, DialogFooter, Stack, Text } from '@chakra-ui/react';
import { Plus, X } from '@phosphor-icons/react';
import { v4 as uuidV4 } from 'uuid';

import { DialogBody, DialogContent, DialogRoot } from '@/components/ui/dialog';
import { ApplicationQuestion } from '@/types/ApplicationQuestion';
import { ApplicationQuestionType } from '@/types/enums/ApplicationQuestionType';
import QuestionEditor from './QuestionEditor';

interface ApplicationQuestionsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (questions: ApplicationQuestion[]) => void;
  initialQuestions?: ApplicationQuestion[];
  isLoading: boolean;
  error: string | null;
}

export default function ApplicationQuestionsDialog({
  isOpen,
  onClose,
  onConfirm,
  initialQuestions = [],
  isLoading,
  error
}: ApplicationQuestionsDialogProps) {
  const [questions, setQuestions] = useState<ApplicationQuestion[]>([]);
  const [questionErrors, setQuestionErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    if (isOpen) {
      // Initialize with existing questions or start with one empty question
      if (initialQuestions.length > 0) {
        setQuestions(initialQuestions);
      } else {
        setQuestions([
          {
            question_id: uuidV4(),
            question: '',
            question_type: 'text',
            required: false
          }
        ]);
      }
      setQuestionErrors({});
    }
  }, [isOpen, initialQuestions]);

  const handleAddQuestion = () => {
    setQuestions([
      ...questions,
      {
        question_id: uuidV4(),
        question: '',
        question_type: 'text',
        required: false
      }
    ]);
  };

  const handleRemoveQuestion = (questionId: string) => {
    if (questions.length === 1) {
      // Don't allow removing the last question, just clear it
      setQuestions([
        {
          question_id: uuidV4(),
          question: '',
          question_type: 'text',
          required: false
        }
      ]);
    } else {
      setQuestions(questions.filter((q) => q.question_id !== questionId));
    }
    // Clear error for removed question
    const newErrors = { ...questionErrors };
    delete newErrors[questionId];
    setQuestionErrors(newErrors);
  };

  const handleQuestionChange = (questionId: string, value: string) => {
    setQuestions(
      questions.map((q) => (q.question_id === questionId ? { ...q, question: value } : q))
    );
    // Clear error when user starts typing
    if (questionErrors[questionId]) {
      const newErrors = { ...questionErrors };
      delete newErrors[questionId];
      setQuestionErrors(newErrors);
    }
  };

  const handleQuestionTypeChange = (questionId: string, type: ApplicationQuestionType) => {
    setQuestions(
      questions.map((q) => {
        if (q.question_id === questionId) {
          const updated: ApplicationQuestion = { ...q, question_type: type };
          // Remove options if switching away from multiple_choice
          if (type !== 'multiple_choice') {
            delete updated.options;
          } else if (!updated.options) {
            // Add default options for multiple_choice
            updated.options = [''];
          }
          return updated;
        }
        return q;
      })
    );
  };

  const handleOptionChange = (questionId: string, optionIndex: number, value: string) => {
    setQuestions(
      questions.map((q) => {
        if (q.question_id === questionId && q.options) {
          const newOptions = [...q.options];
          newOptions[optionIndex] = value;
          return { ...q, options: newOptions };
        }
        return q;
      })
    );
  };

  const handleAddOption = (questionId: string) => {
    setQuestions(
      questions.map((q) => {
        if (q.question_id === questionId) {
          return { ...q, options: [...(q.options || []), ''] };
        }
        return q;
      })
    );
  };

  const handleRemoveOption = (questionId: string, optionIndex: number) => {
    setQuestions(
      questions.map((q) => {
        if (q.question_id === questionId && q.options) {
          const newOptions = q.options.filter((_, i) => i !== optionIndex);
          // Keep at least one option
          return { ...q, options: newOptions.length > 0 ? newOptions : [''] };
        }
        return q;
      })
    );
  };

  const handleConfirm = () => {
    // Validate questions
    const errors: { [key: string]: string } = {};
    const nonEmptyQuestions = questions.filter((q) => q.question.trim());

    // Check if there are any empty questions among non-empty ones
    questions.forEach((q) => {
      if (!q.question.trim() && questions.length > 1) {
        errors[q.question_id] = 'Question cannot be empty';
      }
      // Validate multiple choice questions have at least one non-empty option
      if (q.question_type === 'multiple_choice' && q.question.trim()) {
        const nonEmptyOptions = (q.options || []).filter((opt) => opt.trim());
        if (nonEmptyOptions.length === 0) {
          errors[q.question_id] = 'Multiple choice questions must have at least one option';
        }
      }
    });

    if (Object.keys(errors).length > 0) {
      setQuestionErrors(errors);
      return;
    }

    // Clean up options for multiple choice questions (remove empty options)
    const cleanedQuestions = nonEmptyQuestions.map((q) => {
      if (q.question_type === 'multiple_choice' && q.options) {
        return {
          ...q,
          required: true,
          options: q.options.filter((opt) => opt.trim())
        };
      }
      return {
        ...q,
        required: true
      };
    });

    // If all questions are empty, pass empty array
    // Otherwise, pass only non-empty questions
    onConfirm(cleanedQuestions);
  };

  const handleSkip = () => {
    // Skip without any questions
    onConfirm([]);
  };

  return (
    <DialogRoot
      size="xl"
      open={isOpen}
      onOpenChange={(e) => !e.open && !isLoading && onClose()}
      closeOnInteractOutside={!isLoading}
    >
      <DialogContent borderRadius="12px" p="20px 12px 12px 12px" maxH="80vh">
        <DialogBody overflowY="auto" maxH="calc(80vh - 120px)">
          <Stack gap="16px" w="100%">
            <Text fontSize="20px" fontWeight="bold" color="neutrals.900">
              Application Questions
            </Text>

            <Text fontSize="16px" color="neutrals.700">
              Add custom questions for candidates to answer when applying for this job. These
              questions will appear on the public job application form.
            </Text>

            <Stack gap="24px" mt="8px">
              {questions.map((question, index) => (
                <QuestionEditor
                  key={question.question_id}
                  question={question}
                  index={index}
                  isDisabled={isLoading}
                  error={questionErrors[question.question_id]}
                  onQuestionChange={handleQuestionChange}
                  onQuestionTypeChange={handleQuestionTypeChange}
                  onRemoveQuestion={handleRemoveQuestion}
                  onOptionChange={handleOptionChange}
                  onAddOption={handleAddOption}
                  onRemoveOption={handleRemoveOption}
                />
              ))}
            </Stack>

            <Button
              variant="outline"
              size="md"
              onClick={handleAddQuestion}
              disabled={isLoading}
              borderRadius="8px"
              borderColor="primary.900"
              color="primary.900"
              fontSize="16px"
              _hover={{ bg: 'primary.50' }}
              w="fit-content"
            >
              <Plus size={18} weight="bold" />
              Add Question
            </Button>

            {error && (
              <Text color="error.500" fontSize="14px">
                {error}
              </Text>
            )}
          </Stack>
        </DialogBody>
        <DialogFooter gap="12px">
          <Button
            variant="outline"
            border="1px solid"
            borderColor="neutrals.300"
            color="neutrals.700"
            bg="neutrals.0"
            borderRadius="8px"
            fontSize="16px"
            _hover={{ bg: 'neutrals.100' }}
            onClick={handleSkip}
            disabled={isLoading}
          >
            Skip
          </Button>
          <Button
            borderRadius="8px"
            bg="primary.900"
            color="neutrals.0"
            fontSize="16px"
            _hover={{ bg: 'primary.700' }}
            onClick={handleConfirm}
            loading={isLoading}
            disabled={isLoading}
          >
            Save & Continue
          </Button>
        </DialogFooter>
        <DialogCloseTrigger m="12px" disabled={isLoading} cursor="pointer">
          <X size={20} weight="bold" />
        </DialogCloseTrigger>
      </DialogContent>
    </DialogRoot>
  );
}
